import { orderBy } from 'lodash';
import { SystemLocale } from '@ninja/utilities';
import { GetQuoteListResponse, PlanDetailsSchema } from '@shory-health-individual/customer-api';
import { sum } from '../../../../common';
import { AppInit } from '../../../../server';
import { PlanPrice } from '../../quote-benefits-modal';
import { PlanKeyBenefits, getKeyBenefits } from './get-key-benefits';
import { getNetworkCount } from './get-network-count';

export enum InvestorImpactEnum {
	UnImpacted = 0,
	ImpactedButVisible = 1,
	ImpactedButNotVisible = 2,
}

export type CoPayment = {
	coPaymentsDiagnostic: string;
	coPaymentsPharmacy: string;
	coPaymentsLabTests: string;
	coPaymentsOPConsultations: string;
	coPaymentsPhysioTreatment: string;
	coPaymentsInPatient: string;
	coPaymentsMaternity: string;
	coPaymentsDental: string;
	coPaymentsOptical: string;
};

type QuoteCardSchema = {
	planTotalPrice: number;
	planId: number;
	planName: string;
	shortName: string;
	keyBenefits: PlanKeyBenefits;
	icLogoUrl: string;
	annualLimits: {
		cover: number;
		pharmacy: number;
	};
	networks: {
		networkMapping: {
			facilityName: string;
			facilityTypeId: number;
			count: number;
			display: boolean;
		}[];
		topHospitals: string;
	};
	networkGroupId: number;
	planPrices: PlanPrice[];
	quotePlanReferenceNumber: string;
	quoteReferenceNumber: string;
	premiumAmount: number;
	vatAmount: number;
	policyWordingDocumentURL: string;
	isInvestorPartnerOnly: InvestorImpactEnum;
};

export type QuotePlanSchema = QuoteCardSchema &
	CoPayment & {
		planExclusionMappings: PlanDetailsSchema['planExclusionMappings'];
		planBenefits: PlanDetailsSchema['planBenefits'];
		benefitSummary: PlanDetailsSchema['benefitSummary'];
		planNetworkCounts: PlanDetailsSchema['planNetworkCounts'];
		insuranceCompanyId: PlanDetailsSchema['insuranceCompanyId'];
		planKeyBenefits: PlanDetailsSchema['planKeyBenefits'];
		planTypeIds: PlanDetailsSchema['planTypeIds'];
	};

export type GroupedQuotes = { selectedActivePlan: number; quotePlans: QuotePlanSchema[] }[];

const mapQuote = (
	locale: SystemLocale,
	quote: GetQuoteListResponse['quotePlans'][number],
	quoteList: GetQuoteListResponse
): QuotePlanSchema => {
	const planDetails = quote.planDetails;

	const appInit = new AppInit(locale);
	const appInitPlan = appInit.getPlanById(quote.planId);

	return {
		planId: quote.planId,
		networkGroupId: quote.networkGroupId,
		quotePlanReferenceNumber: quote.quotePlanReferenceNumber,
		premiumAmount: quote.premiumAmount,
		vatAmount: quote.vatAmount,

		planExclusionMappings: planDetails.planExclusionMappings,
		planBenefits: planDetails.planBenefits,
		planNetworkCounts: planDetails.planNetworkCounts,
		insuranceCompanyId: planDetails.insuranceCompanyId,
		planKeyBenefits: planDetails.planKeyBenefits,
		planTypeIds: planDetails.planTypeIds,

		quoteReferenceNumber: quoteList?.quoteResponse?.quoteRequestReferenceNumber,
		planPrices: quoteList.quotePlansPrices
			.filter((v) => v.planId === quote.planId)
			.map((planPrice) => ({ ...planPrice, memberId: planPrice.memberid })),

		planTotalPrice: sum(quote.premiumAmount, quote.vatAmount),
		keyBenefits: getKeyBenefits(planDetails.planKeyBenefits, locale),
		icLogoUrl: appInit.getInsuranceCompanyById(planDetails.insuranceCompanyId)?.logoUrl ?? '',
		benefitSummary: appInitPlan?.benefitSummary ?? [],
		planName: appInitPlan?.name ?? '',
		shortName: appInitPlan?.shortName ?? '',
		annualLimits: {
			cover: Number(appInitPlan?.annualLimitCover),
			pharmacy: Number(appInitPlan?.annualLimitPharmacy),
		},

		coPaymentsDiagnostic: appInitPlan?.coPaymentsDiagnostic ?? '',
		coPaymentsPharmacy: appInitPlan?.coPaymentsPharmacy ?? '',
		coPaymentsLabTests: appInitPlan?.coPaymentsLabTests ?? '',
		coPaymentsInPatient: appInitPlan?.coPaymentsInPatient ?? '',
		coPaymentsOPConsultations: appInitPlan?.coPaymentsOPConsultations ?? '',
		coPaymentsPhysioTreatment: appInitPlan?.coPaymentsPhysioTreatment ?? '',
		coPaymentsMaternity: appInitPlan?.coPaymentsMaternity ?? '',
		coPaymentsDental: appInitPlan?.coPaymentsDental ?? '',
		coPaymentsOptical: appInitPlan?.coPaymentsOptical ?? '',
		isInvestorPartnerOnly: appInitPlan?.isInvestorPartnerOnly ?? InvestorImpactEnum.UnImpacted,

		networks: {
			networkMapping: getNetworkCount(planDetails.planNetworkCounts, locale),
			topHospitals: appInitPlan?.topHospitals ?? '',
		},
		policyWordingDocumentURL:
			locale === 'en' ? appInitPlan?.policyWordingUrlEn ?? '' : appInitPlan?.policyWordingUrlAr ?? '',
	};
};

export const mapQuotes = (locale: SystemLocale, quoteList: GetQuoteListResponse) => {
	const allQuotes = quoteList.quotePlans.map((quote) => mapQuote(locale, quote, quoteList));
	const sortedPlans = orderBy(allQuotes, 'premiumAmount', 'asc');

	return sortedPlans;
};
